import React, { useState, useEffect, useCallback } from 'react';
import { Clock, MapPin, Wifi, WifiOff, AlertCircle, UserPlus } from 'lucide-react';

export default function PanelQR() {
  const [qrData, setQrData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(300);
  const [currentTime, setCurrentTime] = useState('Cargando...');
  const [currentDate, setCurrentDate] = useState('');
  const [isOnline, setIsOnline] = useState(true);
  const [recentMarks, setRecentMarks] = useState([]);
  const [empleadosPresentes, setEmpleadosPresentes] = useState({ total: 0, empleados: [] });
  const [errorMsg, setErrorMsg] = useState('');

  // URLs de producción
  const API_URL = process.env.REACT_APP_API_URL || 'https://asistencia-backend.emarodri834.workers.dev/api';
  const FRONTEND_URL = 'https://sistema-asistencia-restaurante.pages.dev';
  
  // Obtener hora del servidor backend
  const fetchServerTime = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/hora-colombia`);
      const data = await response.json();
      setCurrentTime(data.hora);
      setCurrentDate(data.fecha);
    } catch (error) {
      console.error('Error obteniendo hora del servidor:', error);
    }
  }, [API_URL]);
  
  // Generar nuevo QR
  const generateQR = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/generar-qr`, {
        method: 'GET',
        mode: 'cors',
      });

      const data = await response.json();
      
      if (data.token) {
        setQrData(data);
        setTimeRemaining(data.valid_for_seconds);
        setErrorMsg('');
      }
    } catch (error) {
      console.error('Error generando QR:', error);
      setErrorMsg('Error de conexión. Reintentando...');
      setIsOnline(false);
    }
  }, [API_URL]);

  // Obtener marcajes recientes
  const fetchRecentMarks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/marcajes/hoy`, {
        method: 'GET',
        mode: 'cors',
      });
      const data = await response.json();
      setRecentMarks(data.slice(0, 5));
      setIsOnline(true);
    } catch (error) {
      console.error('Error obteniendo marcajes:', error);
    }
  }, [API_URL]);

  // Obtener empleados presentes hoy
  const fetchEmpleadosPresentes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/empleados/presentes`, {
        method: 'GET',
        mode: 'cors',
      });
      const data = await response.json();
      setEmpleadosPresentes(data);
    } catch (error) {
      console.error('Error obteniendo empleados presentes:', error);
    }
  }, [API_URL]);

  // Actualizar hora del servidor cada segundo
  useEffect(() => {
    // Fetch inmediato
    fetchServerTime();
    
    // Actualizar cada 1 segundo
    const timer = setInterval(fetchServerTime, 1000);
    
    return () => clearInterval(timer);
  }, [fetchServerTime]);

  // Inicialización
  useEffect(() => {
    generateQR();
    fetchRecentMarks();
    fetchEmpleadosPresentes();
    
    const qrInterval = setInterval(generateQR, 5 * 60 * 1000);
    const marksInterval = setInterval(() => {
      fetchRecentMarks();
      fetchEmpleadosPresentes();
    }, 10000);
    
    return () => {
      clearInterval(qrInterval);
      clearInterval(marksInterval);
    };
  }, [generateQR, fetchRecentMarks, fetchEmpleadosPresentes]);

  // Contador de tiempo QR
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Regenerar cuando expire
  useEffect(() => {
    if (timeRemaining === 0) {
      generateQR();
    }
  }, [timeRemaining, generateQR]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressColor = () => {
    const percentage = (timeRemaining / 300) * 100;
    if (percentage > 50) return 'bg-green-500';
    if (percentage > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">🍽️ Enruanados Gourmet</h1>
            <p className="text-xl opacity-80">Sistema de Asistencia</p>
          </div>
          
          <div className="text-right">
            {/* ✅ HORA DEL SERVIDOR - Siempre correcta */}
            <div className="text-5xl font-mono font-bold mb-2">
              {currentTime}
            </div>
            <div className="text-lg opacity-80">
              {currentDate}
            </div>
            
            <div className="flex items-center justify-end gap-2 mt-2">
              {isOnline ? (
                <>
                  <Wifi className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-green-400">En línea</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-red-400">Sin conexión</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Escanea para marcar asistencia</h2>
                <p className="text-xl opacity-80">Acerca tu celular al código QR</p>
              </div>

              <div className="flex justify-center mb-8">
                {qrData ? (
                  <div className="bg-white p-8 rounded-2xl shadow-xl">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(`${FRONTEND_URL}/marcar?token=${qrData.token}`)}`}
                      alt="QR Code"
                      className="w-96 h-96"
                    />
                  </div>
                ) : (
                  <div className="w-96 h-96 bg-white/20 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <Clock className="w-16 h-16 mx-auto mb-4 animate-spin" />
                      <p className="text-xl">Generando código...</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg opacity-80">Código válido por:</span>
                  <span className="text-3xl font-mono font-bold">{formatTime(timeRemaining)}</span>
                </div>
                
                <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor()} transition-all duration-1000 ease-linear`}
                    style={{ width: `${(timeRemaining / 300) * 100}%` }}
                  />
                </div>

                {timeRemaining < 60 && (
                  <div className="flex items-center gap-2 text-yellow-400 animate-pulse">
                    <AlertCircle className="w-5 h-5" />
                    <span>El código expirará pronto. Escanea ahora.</span>
                  </div>
                )}
              </div>

              {errorMsg && (
                <div className="mt-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-center">
                  {errorMsg}
                </div>
              )}
            </div>

            <div className="mt-6 bg-blue-500/20 backdrop-blur-lg rounded-2xl p-6 border border-blue-500/30">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                Instrucciones
              </h3>
              <ul className="space-y-2 text-lg opacity-90">
                <li>✅ Abre la cámara de tu celular</li>
                <li>✅ Apunta al código QR</li>
                <li>✅ Selecciona tu nombre</li>
                <li>✅ Confirma tu marcaje</li>
                <li>⚠️ Debes estar dentro del restaurante</li>
              </ul>
            </div>

            <div className="mt-6 bg-green-500/20 backdrop-blur-lg rounded-2xl p-6 border border-green-500/30">
              <h3 className="text-xl font-bold mb-4 text-center flex items-center justify-center gap-2">
                <UserPlus className="w-6 h-6" />
                ¿Eres nuevo empleado?
              </h3>
              <a
                href="/registro"
                className="block w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 text-center text-lg"
              >
                📝 Registrarme como Empleado
              </a>
              <p className="text-sm text-center mt-3 opacity-80">
                Completa tus datos y espera la aprobación del administrador
              </p>
              <div className="mt-4 p-3 bg-purple-500/20 border border-purple-500/40 rounded-lg">
                <p className="text-xs text-center">
                  💡 <strong>Meseros:</strong> Se generarán automáticamente tus credenciales para el sistema FUDO
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h4 className="text-xl font-bold mb-4">📊 Empleados Presentes Hoy</h4>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-white/10">
                  <span className="opacity-80">Total marcajes:</span>
                  <span className="text-2xl font-bold">{recentMarks.length}</span>
                </div>
                
                <div className="flex justify-between items-center pb-3 border-b border-white/10">
                  <span className="opacity-80">Han marcado:</span>
                  <span className="text-2xl font-bold text-green-400">
                    {empleadosPresentes.total}
                  </span>
                </div>

                {empleadosPresentes.empleados.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm opacity-70 mb-2">Lista de asistencia:</p>
                    <div className="space-y-1 max-h-96 overflow-y-auto">
                      {empleadosPresentes.empleados.map((nombre, idx) => (
                        <div 
                          key={idx}
                          className="bg-white/5 rounded px-3 py-2 text-sm"
                        >
                          ✓ {nombre}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
